import * as studio from '~/models/studio';
import { isDef } from '~/util';
import { exportWaveform, waveformsDuration } from './waveform';

declare const __PULSER_STUDIO_VERSION__: string;
import type * as pulserSchema from '~/models/pulser-schema';
export * from './waveform';
import type {
  OpDelay,
  Operation,
  OpPulse,
  OpTarget
} from '~/models/pulser-schema';

/**
 * Export internal experiment data to a common Pulser format.
 */
export function exportToPulser(
  data: studio.StudioExperiment
): pulserSchema.PulserSequence {
  const targetMap = Object.fromEntries(
    data.register.map((r, i) => [r.uuid, i])
  );
  const pulses: typeof data.pulses = data.pulses.map((p) => {
    let pulse = { ...p };
    if (pulse.detuning !== null) {
      const ampDuration = waveformsDuration(pulse.amplitude);
      const detDuration = waveformsDuration(pulse.detuning);

      const diff = ampDuration - detDuration;
      if (diff > 0) {
        const detuning = [...pulse.detuning];
        detuning.push({
          kind: 'constant',
          constraint: { kind: 'area', maxVal: 0, duration: Math.abs(diff) }
        });
        pulse = { ...pulse, detuning };
      } else if (diff < 0) {
        const amplitude = [...pulse.amplitude];
        amplitude.push({
          kind: 'constant',
          constraint: { kind: 'area', maxVal: 0, duration: Math.abs(diff) }
        });
        pulse = { ...pulse, amplitude };
      }
    }

    return pulse;
  });
  const pulseMap = Object.fromEntries(pulses.map((p, i) => [p.uuid, i]));
  const channelUniqueNames = new Set();
  const channelMap = Object.fromEntries(
    data.channels.map((r) => {
      let name = r.label;
      let i = 0;
      while (channelUniqueNames.has(name)) {
        name = `${r.label} (${i++})`;
      }
      channelUniqueNames.add(name);
      return [r.uuid, name];
    })
  );

  const operations: pulserSchema.Operation[] = [];

  const lastTarget = new Map<string, string>();

  for (const pulse of pulses) {
    if (pulse.targetUuid != null) {
      if (lastTarget.get(pulse.channelUuid) != pulse.targetUuid) {
        lastTarget.set(pulse.channelUuid, pulse.targetUuid);
        operations.push({
          op: 'target',
          channel: channelMap[pulse.channelUuid],
          target: targetMap[pulse.targetUuid]
        });
      }
    }

    if (pulse.dependencies.length > 0) {
      const selfIdx = targetMap[pulse.uuid];
      const deps = pulse.dependencies
        .map((pUuid) => {
          const idx = pulseMap[pUuid];
          if (idx == null || idx > selfIdx) {
            return null;
          }
          const channel = pulses[idx].channelUuid;
          return channelMap[channel];
        })
        .filter(isDef);
      if (deps.length > 0) {
        deps.push(channelMap[pulse.channelUuid]);
        operations.push({
          op: 'align',
          channels: [...new Set(deps)]
        });
      }
    }

    if (pulse.delay > 0) {
      operations.push({
        op: 'delay',
        channel: channelMap[pulse.channelUuid],
        time: pulse.delay
      });
    }

    operations.push({
      op: 'pulse',
      protocol: pulse.protocol,
      channel: channelMap[pulse.channelUuid],
      amplitude: exportWaveforms(pulse.amplitude),
      detuning: exportWaveforms(pulse.detuning),
      phase: pulse.phase,
      post_phase_shift: pulse.postPhaseShift
    });
  }

  // Get pulser-studio version (injected at build time from package.json via Vite define)
  const pulserStudioVersion = __PULSER_STUDIO_VERSION__;

  // Merge metadata: preserve original metadata and add/update pulser-studio version
  const mergedMetadata = {
    ...data.metadata,
    package_versions: {
      ...data.metadata?.package_versions,
      'pulser-studio': pulserStudioVersion
    }
  };

  return removeUnusedChannels({
    version: '1',
    name: data.name,
    device: exportDevice(data),
    register: data.register.map((r) => ({
      name: r.label,
      x: r.pos.x,
      y: r.pos.y
    })),
    channels: Object.fromEntries(
      data.channels.map((c) => [
        channelMap[c.uuid],
        `${studio.getHardwareChannelId(c)}`
      ])
    ),
    variables: {}, // variables not supported yet
    operations,
    measurement:
      data.basis === studio.Basis.Digital
        ? 'digital'
        : data.basis === studio.Basis.XY
          ? 'XY'
          : 'ground-rydberg',
    metadata: mergedMetadata
  });
}

function exportDevice({
  device,
  channels
}: studio.StudioExperiment): pulserSchema.Device {
  return {
    version: '1',
    name: device.name,
    is_virtual: true,
    dimensions: device.dimensions as 2 | 3,
    rydberg_level: device.rydbergLevel,
    max_atom_num: device.maxAtomNum,
    max_radial_distance: device.maxRadialDistance,
    min_atom_distance: device.minAtomDistance,
    supports_slm_mask: true,
    interaction_coeff_xy: null,
    max_layout_filling: 0.5,
    reusable_channels: true,
    channels: channels.map(exportDeviceChannel)
  };
}

function exportDeviceChannel(channel: studio.Channel): pulserSchema.Channel {
  return {
    id: studio.getHardwareChannelId(channel),
    max_abs_detuning: channel.maxAbsDetuning,
    max_amp: channel.maxAmp,
    clock_period: channel.clockPeriod,
    min_duration: channel.minDuration,
    max_duration: channel.maxDuration ?? null,
    mod_bandwidth: channel.modBandwidth ?? null,
    eom_config: null,
    basis:
      channel.basis === studio.Basis.GroundRydberg
        ? 'ground-rydberg'
        : channel.basis === studio.Basis.XY
          ? 'XY'
          : 'digital',
    addressing: channel.addressing === 'global' ? 'Global' : 'Local',
    fixed_retarget_t:
      channel.addressing === 'global' ? null : channel.fixedRetargetT || 0,
    max_targets:
      channel.addressing === 'global' ? null : channel.maxTargets || 0,
    min_retarget_interval:
      channel.addressing === 'global' ? null : channel.minRetargetInterval || 0
  };
}

function exportWaveforms(
  waveforms: readonly studio.Waveform[] | null
): pulserSchema.Waveform {
  if (waveforms == null) {
    // empty detuning case - constant zero
    return {
      kind: 'constant',
      value: 0,
      duration: 0
    };
  }
  if (waveforms.length != 1) {
    return {
      kind: 'composite',
      waveforms: waveforms.map(exportWaveform)
    };
  }
  return exportWaveform(waveforms[0]);
}

function isSingleChannelOperation(
  operation: Operation
): operation is OpDelay | OpTarget | OpPulse {
  return 'channel' in operation;
}

/**
 * Remove channels with no pulses from a serialized sequence to avoid errors on emulators
 */
function removeUnusedChannels(sequence: pulserSchema.PulserSequence) {
  const { operations, channels } = sequence;

  const usedChannelIds = new Set(
    operations
      .filter(isSingleChannelOperation)
      .map((operation) => operation.channel)
  );

  const usedChannels = Object.fromEntries(
    Object.entries(channels).filter(([channelId, _channel]) =>
      usedChannelIds.has(channelId)
    )
  );

  return {
    ...sequence,
    channels: usedChannels
  };
}
